import axios from '@axios'
import { pagination } from '@/mixins'
import moment from 'moment'

const getInitialState = () => ({
  summary: null,
  compliance: [],
  compliancePaging: pagination.data().paging,
  divergences: [],
  divergencePaging: pagination.data().paging,
  filters: {
    period: {
      startDate: moment().subtract(30, 'day').startOf('day').format(),
      endDate: moment().endOf('day').format(),
    },
    storeIds: [],
    groupId: null,
    brandId: null,
    daysOfWeek: [],
    onlyNonCompliant: false,
    onlyWithDivergence: false,
    minDivergentSkusPercent: null,
  },
})

/** Filtros comuns às três chamadas do dashboard, para que os cards e os dois grids
 * sempre reflitam o mesmo recorte. */
const commonParams = filters => {
  const params = {
    from: filters.period.startDate,
    to: filters.period.endDate,
    groupId: filters.groupId,
    brandId: filters.brandId,
  }

  // Arrays vazios são omitidos: o querystring.stringify geraria "storeIds=",
  // que o binding do .NET tentaria converter para int e devolveria 400.
  if (filters.storeIds?.length) params.storeIds = filters.storeIds
  if (filters.daysOfWeek?.length) params.daysOfWeek = filters.daysOfWeek

  return params
}

export default {
  namespaced: true,
  state: getInitialState(),

  getters: {},

  mutations: {
    SET_SUMMARY(state, summary) {
      state.summary = summary
    },
    SET_COMPLIANCE(state, compliance) {
      state.compliance = compliance
    },
    SET_COMPLIANCE_PAGING(state, paging) {
      state.compliancePaging = paging
    },
    SET_COMPLIANCE_CURRENT_PAGE(state, currentPage) {
      state.compliancePaging.currentPage = currentPage
    },
    SET_COMPLIANCE_PAGE_SIZE(state, pageSize) {
      state.compliancePaging.currentPage = 1
      state.compliancePaging.pageSize = pageSize
    },
    SET_DIVERGENCES(state, divergences) {
      state.divergences = divergences
    },
    SET_DIVERGENCE_PAGING(state, paging) {
      state.divergencePaging = paging
    },
    SET_DIVERGENCE_CURRENT_PAGE(state, currentPage) {
      state.divergencePaging.currentPage = currentPage
    },
    SET_DIVERGENCE_PAGE_SIZE(state, pageSize) {
      state.divergencePaging.currentPage = 1
      state.divergencePaging.pageSize = pageSize
    },
    SET_FILTERS(state, filters) {
      state.filters = filters
    },
    CLEAN_FILTERS(state) {
      const { filters } = getInitialState()
      state.filters = filters
    },
    CLEAN_STATE(state) {
      const initialState = getInitialState()
      state.filters = initialState.filters
      state.summary = initialState.summary
      state.compliance = initialState.compliance
      state.compliancePaging = initialState.compliancePaging
      state.divergences = initialState.divergences
      state.divergencePaging = initialState.divergencePaging
    },
  },

  actions: {
    async fetchSummary({ commit, state }) {
      const { data } = await axios.get('/api/stock-counting-dashboard/summary', {
        params: commonParams(state.filters),
      })

      commit('SET_SUMMARY', data)
    },

    async fetchCompliance({ commit, state }) {
      const { data } = await axios.get('/api/stock-counting-dashboard/compliance', {
        params: {
          ...commonParams(state.filters),
          onlyNonCompliant: state.filters.onlyNonCompliant,
          pageSize: state.compliancePaging.pageSize,
          pageIndex: state.compliancePaging.currentPage - 1,
        },
      })

      commit('SET_COMPLIANCE', data.results || [])
      commit('SET_COMPLIANCE_PAGING', {
        ...state.compliancePaging,
        rowCount: data.rowCount,
        rowsInCurrentPage: data.rowsInCurrentPage,
      })
    },

    async fetchDivergences({ commit, state }) {
      const { data } = await axios.get('/api/stock-counting-dashboard/divergences', {
        params: {
          ...commonParams(state.filters),
          onlyWithDivergence: state.filters.onlyWithDivergence,
          minDivergentSkusPercent: state.filters.minDivergentSkusPercent,
          pageSize: state.divergencePaging.pageSize,
          pageIndex: state.divergencePaging.currentPage - 1,
        },
      })

      commit('SET_DIVERGENCES', data.results || [])
      commit('SET_DIVERGENCE_PAGING', {
        ...state.divergencePaging,
        rowCount: data.rowCount,
        rowsInCurrentPage: data.rowsInCurrentPage,
      })
    },

    async fetchDashboard({ dispatch }) {
      await Promise.all([
        dispatch('fetchSummary'),
        dispatch('fetchCompliance'),
        dispatch('fetchDivergences'),
      ])
    },

    setFilters({ commit }, filters) {
      commit('SET_FILTERS', filters)
    },

    setComplianceCurrentPage({ commit }, currentPage) {
      commit('SET_COMPLIANCE_CURRENT_PAGE', currentPage)
    },

    setCompliancePageSize({ commit }, pageSize) {
      commit('SET_COMPLIANCE_PAGE_SIZE', pageSize)
    },

    setDivergenceCurrentPage({ commit }, currentPage) {
      commit('SET_DIVERGENCE_CURRENT_PAGE', currentPage)
    },

    setDivergencePageSize({ commit }, pageSize) {
      commit('SET_DIVERGENCE_PAGE_SIZE', pageSize)
    },

    resetFilters({ commit }) {
      commit('CLEAN_FILTERS')
    },

    cleanState({ commit }) {
      commit('CLEAN_STATE')
    },
  },
}
